import { Controller } from "@hotwired/stimulus";
import { Loader } from "@googlemaps/js-api-loader";

export default class extends Controller {
  static targets = [
    "addressComponents",
    "field",
    "frame",
    "map",
    "remove",
    "value",
  ];

  static values = {
    defaultLat: { type: Number, default: 39.5 },
    defaultLng: { type: Number, default: -98.35 },
    zoom: { type: Number, default: 14 },
  };

  async connect() {
    const loader = new Loader({
      apiKey: tito.env.GOOGLE_MAPS_API_KEY,
      version: "weekly",
      libraries: ["maps", "marker", "places"],
    });

    await loader.load();

    this.initAutocomplete();

    if (this.hasMapTarget) {
      this.initMap();
    }
  }

  /**
   * Returns the desired color scheme for the placeAutocomplete panel.
   * Will use "dark" if either <html> or <body> has the class 'dark-mode'
   */
  getPreferredColorScheme() {
    const htmlDark = document.documentElement.classList.contains("dark-mode");
    const bodyDark = document.body.classList.contains("dark-mode");
    if (htmlDark || bodyDark) {
      return "dark";
    }
    return "light";
  }

  initAutocomplete() {
    if (!this.hasFieldTarget) return;

    const placeAutocomplete = new google.maps.places.PlaceAutocompleteElement();
    this.placeAutocomplete = placeAutocomplete; // Save reference for color scheme

    // Set initial color scheme
    placeAutocomplete.style.colorScheme = this.getPreferredColorScheme();

    placeAutocomplete.style.border = "var(--input-border)";
    placeAutocomplete.style.background = "var(--base-input-background-color)";

    const updateColorScheme = () => {
      placeAutocomplete.style.colorScheme = this.getPreferredColorScheme();
    };

    // Listen for "dark-mode" class changes
    // Mutation observer for class changes to html or body
    if (!this._colorSchemeMutationObserver) {
      this._colorSchemeMutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (
            mutation.attributeName === "class" &&
            (mutation.target === document.documentElement ||
              mutation.target === document.body)
          ) {
            updateColorScheme();
          }
        }
      });
      // Observe both html and body for class changes
      this._colorSchemeMutationObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"],
      });
      this._colorSchemeMutationObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    const card = this.fieldTarget;
    card.after(placeAutocomplete);
    card.hidden = true;

    placeAutocomplete.addEventListener(
      "gmp-select",
      async ({ placePrediction }) => {
        const place = placePrediction.toPlace();
        await place.fetchFields({
          fields: [
            "displayName",
            "formattedAddress",
            "location",
            "id",
            "addressComponents",
          ],
        });
        this.placeChanged(place);
      },
    );
  }

  disconnect() {
    // Clean up listeners and observers on disconnect
    // No media query listener any more
    if (this._colorSchemeMutationObserver) {
      this._colorSchemeMutationObserver.disconnect();
      this._colorSchemeMutationObserver = null;
    }
  }

  initMap() {
    if (!this.hasMapTarget) return;

    const lat = parseFloat(this.mapTarget.dataset.lat);
    const lng = parseFloat(this.mapTarget.dataset.lng);

    const mapOptions = {
      center: { lat, lng },
      disableDefaultUI: true,
      scrollwheel: false,
      navigationControl: false,
      mapTypeControl: false,
      scaleControl: false,
      draggable: false,
      zoom: this.zoomValue,
      mapId: "io-map",
    };

    this.map = new google.maps.Map(this.mapTarget, mapOptions);

    // Only create marker if we have lat/lng values
    if (lat && lng) {
      this.marker = new google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        position: { lat, lng },
      });
    }

    // Bind autocomplete to map if autocomplete exists
    if (this.autocomplete) {
      this.autocomplete.bindTo("bounds", this.map);
    }
  }

  placeChanged(place) {
    if (!place) {
      return;
    }

    if (this.hasValueTarget) {
      const out = place.toJSON();
      out.lat = out.location.lat;
      out.lng = out.location.lng;
      delete out.location;
      this.valueTarget.value = JSON.stringify(out);
      this.valueTarget.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  searching(event) {
    if (event.key == "Enter") {
      event.preventDefault();
    }
  }
}
